import * as THREE from 'three';

export class Ocean {
  public mesh: THREE.Mesh;
  private geometry!: THREE.PlaneGeometry;
  private material!: THREE.ShaderMaterial;
  private resolution: 'low' | 'medium' | 'high' = 'medium';
  private size: number = 320;
  private elapsedTime: number = 0;

  // Wave properties
  private waveHeight: number = 1.2; // meters
  private wavePeriod: number = 5.5; // seconds
  private waveDirection: number = 45; // degrees (direction wave comes from)
  private isWireframe: boolean = false;

  constructor() {
    this.createGeometry('medium');
    this.createMaterial();
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.rotation.x = -Math.PI / 2;
    this.mesh.receiveShadow = true;
  }

  private getSegmentsForResolution(res: 'low' | 'medium' | 'high'): number {
    switch (res) {
      case 'low': return 64;
      case 'medium': return 128;
      case 'high': return 200;
    }
  }

  private createGeometry(res: 'low' | 'medium' | 'high') {
    this.resolution = res;
    const segments = this.getSegmentsForResolution(res);
    if (this.geometry) {
      this.geometry.dispose();
    }
    this.geometry = new THREE.PlaneGeometry(this.size, this.size, segments, segments);
  }

  private createMaterial() {
    const vertexShader = `
      uniform float uTime;
      uniform float uWaveHeight;
      uniform float uWavePeriod;
      uniform float uWaveDirRad; // in radians
      
      varying vec3 vWorldPosition;
      varying vec3 vNormal;
      varying float vWaveHeightFrac;
      varying vec2 vUv;

      // Gerstner Wave Calculation
      // P: input point, dir: normalized 2D direction, A: amplitude, L: wavelength, S: speed
      vec3 gerstnerWave(vec2 p, vec2 dir, float A, float L, float speed, inout vec3 tangent, inout vec3 binormal) {
        float k = 2.0 * 3.14159265 / L;
        float c = speed;
        float f = k * (dot(dir, p) - c * uTime);
        float q = 0.4 / (k * A * 4.0 + 0.001); // steepness limiter
        q = clamp(q, 0.1, 0.5);

        float cosF = cos(f);
        float sinF = sin(f);

        tangent += vec3(
          -dir.x * dir.x * (q * A * k * sinF),
          dir.x * (A * k * cosF),
          -dir.x * dir.y * (q * A * k * sinF)
        );

        binormal += vec3(
          -dir.x * dir.y * (q * A * k * sinF),
          dir.y * (A * k * cosF),
          -dir.y * dir.y * (q * A * k * sinF)
        );

        return vec3(
          q * A * dir.x * cosF,
          A * sinF,
          q * A * dir.y * cosF
        );
      }

      void main() {
        vUv = uv;
        vec3 pos = position;
        
        // Base direction from angle
        vec2 mainDir = vec2(sin(uWaveDirRad), cos(uWaveDirRad));
        
        // Secondary cross directions for natural sea surface
        float angleOffset1 = 0.35;
        float angleOffset2 = -0.45;
        vec2 dir2 = vec2(sin(uWaveDirRad + angleOffset1), cos(uWaveDirRad + angleOffset1));
        vec2 dir3 = vec2(sin(uWaveDirRad + angleOffset2), cos(uWaveDirRad + angleOffset2));

        float speedBase = 6.28318 / max(uWavePeriod, 1.0);
        float baseH = uWaveHeight * 0.5;

        vec3 tangent = vec3(1.0, 0.0, 0.0);
        vec3 binormal = vec3(0.0, 0.0, 1.0);

        // 4 octave wave synthesis
        vec3 disp = vec3(0.0);
        disp += gerstnerWave(pos.xy, mainDir, baseH * 0.55, 38.0, speedBase * 4.2, tangent, binormal);
        disp += gerstnerWave(pos.xy, dir2, baseH * 0.28, 21.0, speedBase * 3.1, tangent, binormal);
        disp += gerstnerWave(pos.xy, dir3, baseH * 0.15, 12.0, speedBase * 2.3, tangent, binormal);
        disp += gerstnerWave(pos.xy, mainDir, baseH * 0.08, 6.0, speedBase * 1.5, tangent, binormal);

        pos.x += disp.x;
        pos.y += disp.z;
        pos.z += disp.y; // In PlaneGeometry, normal is Z before rotation

        // Compute analytical normal
        vec3 computedNormal = normalize(cross(binormal, tangent));
        vNormal = normalMatrix * computedNormal;

        vWaveHeightFrac = (disp.y + baseH) / (baseH * 2.0 + 0.001);

        vec4 worldPos = modelMatrix * vec4(pos, 1.0);
        vWorldPosition = worldPos.xyz;

        gl_Position = projectionMatrix * viewMatrix * worldPos;
      }
    `;

    const fragmentShader = `
      uniform vec3 uSunPosition;
      uniform vec3 uSunColor;
      uniform vec3 uMoonPosition;
      uniform vec3 uMoonColor;
      uniform vec3 uDeepWaterColor;
      uniform vec3 uShallowWaterColor;
      uniform vec3 uSkyColor;
      uniform float uTime;
      uniform float uWaveHeight;

      varying vec3 vWorldPosition;
      varying vec3 vNormal;
      varying float vWaveHeightFrac;
      varying vec2 vUv;

      void main() {
        vec3 normal = normalize(vNormal);
        vec3 viewDir = normalize(cameraPosition - vWorldPosition);

        // Fresnel term
        float fresnel = pow(1.0 - max(dot(viewDir, normal), 0.0), 3.5);
        fresnel = clamp(fresnel, 0.05, 0.95);

        // Sun light contribution
        vec3 sunDir = normalize(uSunPosition);
        float sunElevation = max(sunDir.y, 0.0);
        float sunDiff = max(dot(normal, sunDir), 0.0) * sunElevation;
        
        // Sun specular highlight (Blinn-Phong)
        vec3 halfVecSun = normalize(sunDir + viewDir);
        float sunSpec = pow(max(dot(normal, halfVecSun), 0.0), 160.0) * sunElevation;

        // Moon light contribution
        vec3 moonDir = normalize(uMoonPosition);
        float moonElevation = max(moonDir.y, 0.0);
        float moonDiff = max(dot(normal, moonDir), 0.0) * moonElevation * 0.35;
        vec3 halfVecMoon = normalize(moonDir + viewDir);
        float moonSpec = pow(max(dot(normal, halfVecMoon), 0.0), 90.0) * moonElevation * 0.6;

        // Water body color gradient between shallow and deep
        vec3 waterColor = mix(uDeepWaterColor, uShallowWaterColor, clamp(vWaveHeightFrac, 0.0, 1.0));

        // Wave crest foam
        float foamFactor = smoothstep(0.72, 0.98, vWaveHeightFrac) * clamp(uWaveHeight * 0.3, 0.0, 0.8);
        vec3 foamColor = vec3(0.92, 0.96, 1.0);

        // Ambient sky bounce
        vec3 surfaceColor = mix(waterColor, uSkyColor, fresnel * 0.65);
        surfaceColor += (uSunColor * sunDiff * 0.4 + uMoonColor * moonDiff);
        surfaceColor += uSunColor * sunSpec * 1.5 + uMoonColor * moonSpec * 0.8;
        surfaceColor = mix(surfaceColor, foamColor, foamFactor);

        gl_FragColor = vec4(surfaceColor, 0.95);
      }
    `;

    this.material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uWaveHeight: { value: this.waveHeight },
        uWavePeriod: { value: this.wavePeriod },
        uWaveDirRad: { value: THREE.MathUtils.degToRad(this.waveDirection) },
        uSunPosition: { value: new THREE.Vector3(100, 100, 50) },
        uSunColor: { value: new THREE.Color(1.0, 0.95, 0.85) },
        uMoonPosition: { value: new THREE.Vector3(-100, 80, -50) },
        uMoonColor: { value: new THREE.Color(0.3, 0.45, 0.7) },
        uDeepWaterColor: { value: new THREE.Color('#0a213e') },
        uShallowWaterColor: { value: new THREE.Color('#146b80') },
        uSkyColor: { value: new THREE.Color('#82b5d8') }
      },
      wireframe: this.isWireframe,
      transparent: true,
      side: THREE.DoubleSide
    });
  }

  public update(delta: number) {
    this.elapsedTime += delta;
    if (this.material && this.material.uniforms) {
      this.material.uniforms.uTime.value = this.elapsedTime;
    }
  }

  public setWaveHeight(height: number) {
    this.waveHeight = Math.max(0.05, height);
    if (this.material && this.material.uniforms) {
      this.material.uniforms.uWaveHeight.value = this.waveHeight;
    }
  }

  public getWaveHeight(): number {
    return this.waveHeight;
  }

  public setWavePeriod(period: number) {
    this.wavePeriod = Math.max(1.0, period);
    if (this.material && this.material.uniforms) {
      this.material.uniforms.uWavePeriod.value = this.wavePeriod;
    }
  }

  public setWaveDirection(degrees: number) {
    this.waveDirection = degrees;
    if (this.material && this.material.uniforms) {
      this.material.uniforms.uWaveDirRad.value = THREE.MathUtils.degToRad(degrees);
    }
  }

  public setWireframe(enabled: boolean) {
    this.isWireframe = enabled;
    if (this.material) {
      this.material.wireframe = enabled;
    }
  }

  public setResolution(res: 'low' | 'medium' | 'high') {
    if (this.resolution === res) return;
    this.createGeometry(res);
    this.mesh.geometry = this.geometry;
  }

  public updateLighting(
    sunPos: THREE.Vector3,
    sunCol: THREE.Color,
    moonPos: THREE.Vector3,
    moonCol: THREE.Color,
    skyCol: THREE.Color,
    deepCol: THREE.Color,
    shallowCol: THREE.Color
  ) {
    if (!this.material || !this.material.uniforms) return;
    this.material.uniforms.uSunPosition.value.copy(sunPos);
    this.material.uniforms.uSunColor.value.copy(sunCol);
    this.material.uniforms.uMoonPosition.value.copy(moonPos);
    this.material.uniforms.uMoonColor.value.copy(moonCol);
    this.material.uniforms.uSkyColor.value.copy(skyCol);
    this.material.uniforms.uDeepWaterColor.value.copy(deepCol);
    this.material.uniforms.uShallowWaterColor.value.copy(shallowCol);
  }
}
